import React, { memo, useEffect } from "react";
import { useState } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import configServ from "../../../../services/config";
import { useDispatch, useSelector } from "react-redux";
import { triggerLoader } from "../../../../redux/reducers/LoaderTrigger";
import AlertDialog from "../../../AlertDialog/AlertDialog";
import EmailIcon from "@mui/icons-material/Email";
import PrintIcon from "@mui/icons-material/Print";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import InvoiceTabPanel from "../../../Tab/InvoiceTabPanel";
import LoaderNew from "../../../Loader/LoaderNew";
import jsPDF from "jspdf";

import { drawPurchaseOrderToPdf } from "../../../../utility/purchaseOrderPdf";

const PurchaseOrderOrientation = ({
  open,
  setOpen,
  data,
  companyDetails,
}) => {
  //#region
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState("");
  const [page, setPage] = useState(1);
  const [orientation, setOrientation] = useState("portrait");
  const [value, setValue] = useState(0);
  const [isEmail, setIsEmail] = useState(false);
  const [loading, setLoading] = useState(true);
  const { company_id, user_id, is_admin } = useSelector(
    (state) => state.GlobalVariables
  );

  const fetchSupplierEmail = async () => {
    try {
      setLoading(true);
      // Try to get supplier email - if endpoint doesn't exist, we'll catch the error
      if (data?.supplier?.email) {
        setIsEmail(true);
      } else {
        setIsEmail(false);
      }
    } catch (e) {
      console.log(e);
      setIsEmail(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchSupplierEmail();
    }
  }, [open]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleOpenDialog = (content) => {
    setDialogContent(content);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDialogContent("");
  };

  const handleOrientationChange = (event) => {
    const { name } = event.target;
    setOrientation(name);
  };

  const printPurchaseOrder = async () => {
    try {
      dispatch(triggerLoader());
      const doc = new jsPDF({
        orientation: orientation === "landscape" ? "landscape" : "portrait",
        unit: "mm",
        format: "a4",
      });

      const items = data.items || [];
      const stageUserResponse = await configServ.getUserIdByPo({ po_id: data.id });
      const stageUser = stageUserResponse?.status === 200 ? stageUserResponse.data : {};

      drawPurchaseOrderToPdf(doc, {
        row: data,
        items,
        copyCount: page,
        stageUser,
        companyDetails,
      });

      const pdfDataUrl = doc.output("dataurlstring");
      window.open(pdfDataUrl);
    } catch (e) {
      handleOpenDialog("Error while printing purchase order");
      console.log(e);
    } finally {
      dispatch(triggerLoader());
    }
  };

  const emailPurchaseOrder = async () => {
    try {
      dispatch(triggerLoader());
      const dataToSend = {
        po_id: data.id,
        supplier_id: data.supplier_id,
        user_id: user_id,
        company_id: company_id,
      };

      try {
        const res = await configServ.sendEmailApiPurchaseOrder(dataToSend);

        if (res && res.status === 200) {
          handleOpenDialog("Purchase order sent successfully via email.");
        } else {
          handleOpenDialog(res?.message || "Error while sending email");
        }
      } catch (emailError) {
        // Fallback message if API doesn't exist
        console.log("Email API not available:", emailError);
        handleOpenDialog("Email functionality is not yet implemented for purchase orders.");
      }
    } catch (e) {
      console.log(e);
      handleOpenDialog("Error while sending purchase order via email");
    } finally {
      dispatch(triggerLoader());
    }
  };

  const handleClose = () => {
    setDialogOpen(false);
    setDialogContent("");
    setPage(1);
    setOrientation("portrait");
    setValue(0);
    setLoading(true);
    setIsEmail(false);
    setOpen(false);
  };

  const handlePage = (event) => {
    const data = parseInt(event.target.value);
    setPage(data);
  };

  const handleSendEmail = () => {
    emailPurchaseOrder();
  };
  //#endregion

  return (
    <div>
      <AlertDialog
        open={dialogOpen}
        setOpen={handleCloseDialog}
        title="Alert"
        message={dialogContent}
        buttonTitle="Ok"
      />
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth={true}
        maxWidth="xs"
      >
        <DialogTitle id="alert-dialog-title">
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 0 }}>
            <Tabs value={value} onChange={handleChange} aria-label="basic tabs">
              <Tab
                label="EMAIL"
                id="tab-0"
                aria-controls="tabpanel-0"
                sx={{ fontWeight: 600 }}
              />
              <Tab
                label="PRINT"
                id="tab-1"
                aria-controls="tabpanel-1"
                sx={{ fontWeight: 600 }}
              />
            </Tabs>
          </Box>
          <Box position="absolute" right={16} top={6}>
            <IconButton aria-label="close" onClick={handleClose}>
              <DisabledByDefaultIcon sx={{ fontSize: 32 }} color="error" />
            </IconButton>
          </Box>
        </DialogTitle>
        <InvoiceTabPanel value={value} index={0}>
          <Box textAlign="center">
            <Typography variant="h6" color="textSecondary">
              Save paper, send the purchase order via email!
            </Typography>
            <br />
            {loading ? (
              <LoaderNew />
            ) : (
              <Button
                disabled={!isEmail}
                variant="contained"
                color="primary"
                startIcon={<EmailIcon />}
                onClick={handleSendEmail}
              >
                Send Email
              </Button>
            )}
            {loading === false && isEmail === false && (
              <Typography component={"p"} variant="body" color="error" p={2}>
                No email address found. Please update supplier email to
                continue.
              </Typography>
            )}
          </Box>
        </InvoiceTabPanel>
        <InvoiceTabPanel value={value} index={1}>
          <Stack direction={"column"}>
            <DialogContent>
              <Box mb={1}>
                <FormControl>
                  <FormLabel id="demo-row-radio-buttons-group-label">
                    No. of pages
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={page}
                    onChange={handlePage}
                  >
                    <Stack direction={"row"}>
                      <FormControlLabel
                        value={1}
                        control={<Radio />}
                        label="1"
                      />
                      <FormControlLabel
                        value={2}
                        control={<Radio />}
                        label="2"
                      />
                      <FormControlLabel
                        value={3}
                        control={<Radio />}
                        label="3"
                      />
                      <FormControlLabel
                        value={4}
                        control={<Radio />}
                        label="4"
                      />
                    </Stack>
                  </RadioGroup>
                </FormControl>
              </Box>
              <Box>
                <FormControl component="fieldset">
                  <FormGroup row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={orientation === "landscape"}
                          onChange={handleOrientationChange}
                          name="landscape"
                        />
                      }
                      label="Landscape"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={orientation === "portrait"}
                          onChange={handleOrientationChange}
                          name="portrait"
                        />
                      }
                      label="Portrait"
                    />
                  </FormGroup>
                </FormControl>
              </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "center" }}>
              <Button
                variant="contained"
                startIcon={<PrintIcon />}
                onClick={() => {
                  handleClose();
                  printPurchaseOrder();
                }}
              >
                Print
              </Button>
            </DialogActions>
          </Stack>
        </InvoiceTabPanel>
      </Dialog>
    </div>
  );
};

export default memo(PurchaseOrderOrientation);
